import { generateKeyBetween, generateNKeysBetween } from 'fractional-indexing-jittered';
import { compact } from 'lodash';
import { AppError } from '../errors/AppError';

export namespace FractionalIndex {
  export type OrderKey = string;

  export type Sortable = {
    order?: OrderKey;
  };

  const START_KEY = null;
  const END_KEY = null;

  export function generatePrependedOrderKey(existingKeys: (OrderKey | undefined)[]): OrderKey {
    const sorted = compact([...existingKeys]).sort();
    return generateOrderKeyAfter(sorted, START_KEY);
  }

  export function generateAppendedOrderKey(existingKeys: (OrderKey | undefined)[]): OrderKey {
    const sorted = compact([...existingKeys]).sort();
    const [last] = sorted.slice(-1);
    return generateOrderKeyAfter(sorted, last);
  }

  export function generateOrderKeyAfter(existingKeys: OrderKey[], afterKey: OrderKey | null): OrderKey {
    const [lowerBoundary, upperBoundary] = boundsParams(existingKeys, afterKey);
    return generateKeyBetween(lowerBoundary, upperBoundary);
  }

  export function generateNPrependedOrderKeys(existingKeys: OrderKey[], number: number): OrderKey[] {
    const sorted = [...existingKeys].sort();
    return generateNOrderKeysAfter(sorted, START_KEY, number);
  }

  export function generateNAppendedOrderKeys(existingKeys: OrderKey[], number: number): OrderKey[] {
    const sorted = [...existingKeys].sort();
    const [last] = sorted.slice(-1);
    return generateNOrderKeysAfter(sorted, last, number);
  }

  export function generateNOrderKeysAfter(
    existingKeys: OrderKey[],
    afterKey: OrderKey | null,
    number: number
  ): OrderKey[] {
    const [lowerBoundary, upperBoundary] = boundsParams(existingKeys, afterKey);

    try {
      return generateNKeysBetween(lowerBoundary, upperBoundary, number);
    } catch (error) {
      throw new AppError('unexpected-data', (error as any)?.message ?? error, {
        error,
        existingKeys,
        afterKey,
        lowerBoundary,
        upperBoundary,
      });
    }
  }

  function boundsParams(existingKeys: OrderKey[], afterKey: OrderKey | null): [OrderKey | null, OrderKey | null] {
    const noExistingKeys = existingKeys.length === 0;

    if (noExistingKeys) {
      return [START_KEY, END_KEY];
    }

    const sortedExistingKeys = [...existingKeys].sort();

    if (afterKey === START_KEY) {
      const [first] = sortedExistingKeys;
      return [START_KEY, first];
    }

    const [last] = sortedExistingKeys.slice(-1);
    const lowerBoundary = afterKey || last;
    const lowerBoundaryIndex = sortedExistingKeys.findIndex((order) => order === afterKey);
    const lowerBoundaryNotFound = lowerBoundaryIndex === -1;

    if (lowerBoundaryNotFound) {
      throw new AppError('not-found', `fractional index could not be found`, { afterKey });
    }

    const upperBoundary = lowerBoundary === last ? END_KEY : sortedExistingKeys[lowerBoundaryIndex + 1];
    return [lowerBoundary, upperBoundary];
  }
}
